import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperuserDashboard = ({ user, onLogout }) => {
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [consentLogs, setConsentLogs] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [createUserData, setCreateUserData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'student',
    age: '',
    grade: ''
  });
  const [mappingData, setMappingData] = useState({
    student_id: '',
    parent_id: '',
    teacher_id: '',
    counselor_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, userId: null, userName: '' });
  const [resetPasswordDialog, setResetPasswordDialog] = useState({ open: false, userId: null, userName: '', newPassword: '' });
  const [gradeDialog, setGradeDialog] = useState({ open: false, studentId: null, studentName: '', currentGrade: '', newGrade: '' });
  const [mappingEditDialog, setMappingEditDialog] = useState({ open: false, studentId: null, studentName: '', mapping: null });
  
  // Question Updater state
  const [questionConfig, setQuestionConfig] = useState(null);
  const [questionConfigForm, setQuestionConfigForm] = useState({
    update_frequency: 'weekly',
    subjects: ['Mathematics', 'Science', 'English'],
    grade_levels: ['9th', '10th', '11th', '12th'],
    countries: ['United States']
  });
  const [updaterActive, setUpdaterActive] = useState(true);

  useEffect(() => {
    fetchUsers();
    fetchAnalytics();
    fetchConsentLogs();
    fetchMappings();
    fetchQuestionConfig();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/superuser/users`);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users', error);
    }
  };

  const fetchMappings = async () => {
    try {
      const response = await axios.get(`${API}/superuser/mappings`);
      setMappings(response.data.mappings || []);
    } catch (error) {
      console.error('Failed to fetch mappings', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/superuser/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics', error);
    }
  };

  const fetchConsentLogs = async () => {
    try {
      const response = await axios.get(`${API}/superuser/consent-logs`);
      setConsentLogs(response.data.consent_logs || []);
    } catch (error) {
      console.error('Failed to fetch consent logs', error);
    }
  };

  const fetchQuestionConfig = async () => {
    try {
      const response = await axios.get(`${API}/superuser/question-updater/config`);
      if (response.data.config) {
        setQuestionConfig(response.data.config);
        setQuestionConfigForm({
          update_frequency: response.data.config.update_frequency,
          subjects: response.data.config.subjects,
          grade_levels: response.data.config.grade_levels,
          countries: response.data.config.countries
        });
        setUpdaterActive(response.data.config.is_active);
      }
    } catch (error) {
      console.error('Failed to fetch question config', error);
    }
  };

  const handleSaveQuestionConfig = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/superuser/question-updater/config`, questionConfigForm);
      toast.success('Question updater configuration saved successfully!');
      fetchQuestionConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUpdater = async (isActive) => {
    setLoading(true);
    try {
      await axios.put(`${API}/superuser/question-updater/toggle?is_active=${isActive}`);
      toast.success(`Question updater ${isActive ? 'enabled' : 'disabled'} successfully!`);
      setUpdaterActive(isActive);
      fetchQuestionConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle updater');
    } finally {
      setLoading(false);
    }
  };

  const handleRunUpdaterNow = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/superuser/question-updater/run-now`);
      toast.success('Question updater triggered! This may take several minutes.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run updater');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/superuser/create-user`, {
        ...createUserData,
        age: createUserData.age ? parseInt(createUserData.age) : null
      });
      toast.success('User created successfully!');
      setCreateUserData({
        email: '',
        password: '',
        name: '',
        role: 'student',
        age: '',
        grade: ''
      });
      fetchUsers();
      fetchAnalytics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleMapUsers = async (e) => {
    e.preventDefault();
    
    if (!mappingData.student_id) {
      toast.error('Please select a student');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/superuser/map-users`, {
        student_id: mappingData.student_id,
        parent_id: mappingData.parent_id || null,
        teacher_id: mappingData.teacher_id || null,
        counselor_id: mappingData.counselor_id || null
      });
      toast.success('Users mapped successfully!');
      setMappingData({
        student_id: '',
        parent_id: '',
        teacher_id: '',
        counselor_id: ''
      });
      fetchMappings(); // Refresh mappings list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to map users');
    } finally {
      setLoading(false);
    }
  };

  const getStudents = () => users.filter(u => u.role === 'student');
  const getParents = () => users.filter(u => u.role === 'parent');
  const getTeachers = () => users.filter(u => u.role === 'teacher');
  const getCounselors = () => users.filter(u => u.role === 'counselor');

  const handleDeleteUser = async () => {
    try {
      await axios.delete(`${API}/superuser/delete-user/${deleteDialog.userId}`);
      toast.success('User deleted successfully!');
      setDeleteDialog({ open: false, userId: null, userName: '' });
      fetchUsers();
      fetchAnalytics();
      fetchMappings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordDialog.newPassword || resetPasswordDialog.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await axios.post(`${API}/superuser/reset-password/${resetPasswordDialog.userId}`, {
        new_password: resetPasswordDialog.newPassword
      });
      toast.success('Password reset successfully!');
      setResetPasswordDialog({ open: false, userId: null, userName: '', newPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleUpdateGrade = async () => {
    if (!gradeDialog.newGrade) {
      toast.error('Please enter a grade');
      return;
    }

    try {
      await axios.put(`${API}/superuser/student/${gradeDialog.studentId}/grade`, null, {
        params: { grade: gradeDialog.newGrade }
      });
      toast.success(`Grade updated to ${gradeDialog.newGrade} successfully!`);
      setGradeDialog({ open: false, studentId: null, studentName: '', currentGrade: '', newGrade: '' });
      fetchUsers(); // Refresh user list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update grade');
    }
  };

  const handleUpdateMapping = async () => {
    try {
      await axios.put(`${API}/superuser/mapping/${mappingEditDialog.studentId}`, {
        student_id: mappingEditDialog.studentId,
        parent_id: mappingEditDialog.mapping.parent_id || null,
        teacher_id: mappingEditDialog.mapping.teacher_id || null,
        counselor_id: mappingEditDialog.mapping.counselor_id || null
      });
      toast.success('Mapping updated successfully!');
      setMappingEditDialog({ open: false, studentId: null, studentName: '', mapping: null });
      fetchMappings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update mapping');
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      student: 'bg-teal-100 text-teal-700',
      parent: 'bg-violet-100 text-violet-700',
      teacher: 'bg-blue-100 text-blue-700',
      professional: 'bg-indigo-100 text-indigo-700',
      counselor: 'bg-pink-100 text-pink-700',
      superuser: 'bg-red-100 text-red-700'
    };
    return colors[role] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-slate-700 to-zinc-800 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Superuser Dashboard</h1>
                <p className="text-sm text-gray-600">System Management</p>
              </div>
            </div>
            <Button
              onClick={onLogout}
              variant="outline"
              className="border-gray-300 hover:bg-gray-100"
              data-testid="logout-button"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Analytics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Users</p>
                <p className="text-3xl font-bold text-slate-700">{analytics.total_users || 0}</p>
              </div>
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Students</p>
                <p className="text-3xl font-bold text-teal-600">{analytics.total_students || 0}</p>
              </div>
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Parents</p>
                <p className="text-3xl font-bold text-violet-600">{analytics.total_parents || 0}</p>
              </div>
              <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Quizzes</p>
                <p className="text-3xl font-bold text-blue-600">{analytics.total_quizzes_taken || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
            </div>
          </Card>
        </div>

        {/* Main Content */}
        <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-lg">
          <Tabs defaultValue="users" className="w-full">
            <TabsList className="w-full justify-start border-b rounded-none bg-transparent h-auto p-0">
              <TabsTrigger
                value="users"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-slate-700 data-[state=active]:bg-transparent"
                data-testid="users-tab"
              >
                All Users
              </TabsTrigger>
              <TabsTrigger
                value="create"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-slate-700 data-[state=active]:bg-transparent"
                data-testid="create-tab"
              >
                Create User
              </TabsTrigger>
              <TabsTrigger
                value="mapping"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-slate-700 data-[state=active]:bg-transparent"
                data-testid="mapping-tab"
              >
                Map Users
              </TabsTrigger>
              <TabsTrigger
                value="consent"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-slate-700 data-[state=active]:bg-transparent"
                data-testid="consent-tab"
              >
                Consent Logs
              </TabsTrigger>
              <TabsTrigger
                value="questions"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-slate-700 data-[state=active]:bg-transparent"
                data-testid="questions-tab"
              >
                Question Updater
              </TabsTrigger>
            </TabsList>

            <TabsContent value="users" className="p-6" data-testid="users-content">
              <h3 className="text-lg font-semibold mb-4">All Users ({users.length})</h3>
              <div className="space-y-3">
                {users.map((u) => (
                  <Card key={u.id} className="p-4 border border-gray-200">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-gray-900">{u.profile?.name || 'N/A'}</h4>
                          <Badge className={getRoleBadgeColor(u.role)}>{u.role}</Badge>
                          <Badge className={u.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                            {u.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600">{u.email}</p>
                        {u.profile && (
                          <p className="text-xs text-gray-500 mt-1">
                            {u.profile.age && `Age: ${u.profile.age}`}
                            {u.profile.grade && ` | Grade: ${u.profile.grade}`}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">ID: {u.id}</p>
                      </div>
                      <div className="flex gap-2">
                        {u.role === 'student' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-green-300 text-green-700 hover:bg-green-50"
                            onClick={() => setGradeDialog({ 
                              open: true, 
                              studentId: u.id, 
                              studentName: u.profile?.name || u.email,
                              currentGrade: u.profile?.grade || 'Not set',
                              newGrade: u.profile?.grade || ''
                            })}
                            data-testid={`change-grade-${u.id}`}
                          >
                            Change Grade
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-blue-300 text-blue-700 hover:bg-blue-50"
                          onClick={() => setResetPasswordDialog({ open: true, userId: u.id, userName: u.profile?.name || u.email, newPassword: '' })}
                          data-testid={`reset-password-${u.id}`}
                        >
                          Reset Password
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteDialog({ open: true, userId: u.id, userName: u.profile?.name || u.email })}
                          disabled={u.id === user?.id}
                          data-testid={`delete-user-${u.id}`}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="create" className="p-6" data-testid="create-content">
              <form onSubmit={handleCreateUser} className="max-w-2xl space-y-4">
                <h3 className="text-lg font-semibold mb-4">Create New User</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="create-name">Full Name</Label>
                    <Input
                      id="create-name"
                      value={createUserData.name}
                      onChange={(e) => setCreateUserData({...createUserData, name: e.target.value})}
                      required
                      data-testid="create-name-input"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="create-email">Email</Label>
                    <Input
                      id="create-email"
                      type="email"
                      value={createUserData.email}
                      onChange={(e) => setCreateUserData({...createUserData, email: e.target.value})}
                      required
                      data-testid="create-email-input"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="create-password">Password</Label>
                    <Input
                      id="create-password"
                      type="password"
                      value={createUserData.password}
                      onChange={(e) => setCreateUserData({...createUserData, password: e.target.value})}
                      required
                      data-testid="create-password-input"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="create-role">Role</Label>
                    <select
                      id="create-role"
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                      value={createUserData.role}
                      onChange={(e) => setCreateUserData({...createUserData, role: e.target.value})}
                      data-testid="create-role-select"
                    >
                      <option value="student">Student</option>
                      <option value="parent">Parent</option>
                      <option value="teacher">Teacher</option>
                      <option value="professional">Professional</option>
                      <option value="counselor">Counselor</option>
                    </select>
                  </div>
                </div>

                {createUserData.role === 'student' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="create-age">Age</Label>
                      <Input
                        id="create-age"
                        type="number"
                        value={createUserData.age}
                        onChange={(e) => setCreateUserData({...createUserData, age: e.target.value})}
                        data-testid="create-age-input"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="create-grade">Grade</Label>
                      <Input
                        id="create-grade"
                        value={createUserData.grade}
                        onChange={(e) => setCreateUserData({...createUserData, grade: e.target.value})}
                        data-testid="create-grade-input"
                        className="mt-1"
                      />
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-slate-700 to-zinc-800 hover:from-slate-800 hover:to-zinc-900 text-white"
                  disabled={loading}
                  data-testid="create-user-button"
                >
                  {loading ? 'Creating...' : 'Create User'}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="mapping" className="p-6" data-testid="mapping-content">
              <div className="space-y-8">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Create New Mapping</h3>
                  <form onSubmit={handleMapUsers} className="max-w-2xl space-y-4">
                    <div>
                      <Label htmlFor="map-student">Select Student (required)</Label>
                      <select
                        id="map-student"
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                        value={mappingData.student_id}
                        onChange={(e) => setMappingData({...mappingData, student_id: e.target.value})}
                        required
                        data-testid="map-student-select"
                      >
                        <option value="">-- Select a Student --</option>
                        {getStudents().map((student) => (
                          <option key={student.id} value={student.id}>
                            {student.profile?.name} ({student.email}) - ID: {student.id.substring(0, 8)}...
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="map-parent">Select Parent (optional)</Label>
                      <select
                        id="map-parent"
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                        value={mappingData.parent_id}
                        onChange={(e) => setMappingData({...mappingData, parent_id: e.target.value})}
                        data-testid="map-parent-select"
                      >
                        <option value="">-- Select a Parent (Optional) --</option>
                        {getParents().map((parent) => (
                          <option key={parent.id} value={parent.id}>
                            {parent.profile?.name} ({parent.email}) - ID: {parent.id.substring(0, 8)}...
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="map-teacher">Select Teacher (optional)</Label>
                      <select
                        id="map-teacher"
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                        value={mappingData.teacher_id}
                        onChange={(e) => setMappingData({...mappingData, teacher_id: e.target.value})}
                        data-testid="map-teacher-select"
                      >
                        <option value="">-- Select a Teacher (Optional) --</option>
                        {getTeachers().map((teacher) => (
                          <option key={teacher.id} value={teacher.id}>
                            {teacher.profile?.name} ({teacher.email}) - ID: {teacher.id.substring(0, 8)}...
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="map-counselor">Select Counselor (optional)</Label>
                      <select
                        id="map-counselor"
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                        value={mappingData.counselor_id}
                        onChange={(e) => setMappingData({...mappingData, counselor_id: e.target.value})}
                        data-testid="map-counselor-select"
                      >
                        <option value="">-- Select a Counselor (Optional) --</option>
                        {getCounselors().map((counselor) => (
                          <option key={counselor.id} value={counselor.id}>
                            {counselor.profile?.name} ({counselor.email}) - ID: {counselor.id.substring(0, 8)}...
                          </option>
                        ))}
                      </select>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-gradient-to-r from-slate-700 to-zinc-800 hover:from-slate-800 hover:to-zinc-900 text-white"
                      disabled={loading}
                      data-testid="map-users-button"
                    >
                      {loading ? 'Mapping...' : 'Create Mapping'}
                    </Button>
                  </form>
                </div>

                <div className="border-t pt-8">
                  <h3 className="text-lg font-semibold mb-4">Existing Mappings ({mappings.length})</h3>
                  {mappings.length === 0 ? (
                    <Card className="p-8 text-center bg-gray-50">
                      <p className="text-gray-500">No mappings created yet</p>
                    </Card>
                  ) : (
                    <div className="space-y-3">
                      {mappings.map((mapping, idx) => (
                        <Card key={mapping.id || idx} className="p-4 border border-gray-200">
                          <div className="flex justify-between items-start">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 flex-1">
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Student</p>
                                <p className="font-semibold text-teal-700">{mapping.student_name}</p>
                                <p className="text-xs text-gray-400">{mapping.student_id.substring(0, 12)}...</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Parent</p>
                                {mapping.parent_id ? (
                                  <>
                                    <p className="font-medium text-violet-700">{mapping.parent_name || 'Unknown'}</p>
                                    <p className="text-xs text-gray-400">{mapping.parent_id.substring(0, 12)}...</p>
                                  </>
                                ) : (
                                  <p className="text-sm text-gray-400">Not assigned</p>
                                )}
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Teacher</p>
                                {mapping.teacher_id ? (
                                  <>
                                    <p className="font-medium text-blue-700">{mapping.teacher_name || 'Unknown'}</p>
                                    <p className="text-xs text-gray-400">{mapping.teacher_id.substring(0, 12)}...</p>
                                  </>
                                ) : (
                                  <p className="text-sm text-gray-400">Not assigned</p>
                                )}
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Counselor</p>
                                {mapping.counselor_id ? (
                                  <>
                                    <p className="font-medium text-pink-700">{mapping.counselor_name || 'Unknown'}</p>
                                    <p className="text-xs text-gray-400">{mapping.counselor_id.substring(0, 12)}...</p>
                                  </>
                                ) : (
                                  <p className="text-sm text-gray-400">Not assigned</p>
                                )}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-blue-300 text-blue-700 hover:bg-blue-50 ml-4"
                              onClick={() => setMappingEditDialog({
                                open: true,
                                studentId: mapping.student_id,
                                studentName: mapping.student_name,
                                mapping: {
                                  parent_id: mapping.parent_id || '',
                                  teacher_id: mapping.teacher_id || '',
                                  counselor_id: mapping.counselor_id || ''
                                }
                              })}
                            >
                              Edit
                            </Button>
                          </div>
                          {mapping.created_at && (
                            <p className="text-xs text-gray-400 mt-2">
                              Created: {new Date(mapping.created_at).toLocaleString()}
                            </p>
                          )}
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="consent" className="p-6" data-testid="consent-content">
              <h3 className="text-lg font-semibold mb-4">Consent Management Logs</h3>
              {consentLogs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p>No consent logs available</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {consentLogs.map((log, idx) => (
                    <Card key={idx} className="p-4 border border-gray-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-900">
                            Child: {log.child_id.substring(0, 8)}...
                          </p>
                          <p className="text-sm text-gray-600">
                            Parent: {log.parent_id.substring(0, 8)}...
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Type: {log.consent_type} | {new Date(log.timestamp).toLocaleString()}
                          </p>
                        </div>
                        <Badge className={log.consent_status ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {log.consent_status ? 'Granted' : 'Revoked'}
                        </Badge>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="questions" className="p-6" data-testid="questions-content">
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Question Database Auto-Updater</h3>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600">
                      Status: <Badge variant={updaterActive ? 'default' : 'secondary'}>
                        {updaterActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </span>
                    <Button
                      variant={updaterActive ? 'destructive' : 'default'}
                      size="sm"
                      onClick={() => handleToggleUpdater(!updaterActive)}
                      disabled={loading}
                    >
                      {updaterActive ? 'Disable' : 'Enable'}
                    </Button>
                  </div>
                </div>

                <Card className="p-6 border border-gray-200">
                  <h4 className="font-semibold mb-4">Configuration</h4>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="frequency">Update Frequency</Label>
                      <select
                        id="frequency"
                        className="w-full mt-1 p-2 border border-gray-300 rounded-md"
                        value={questionConfigForm.update_frequency}
                        onChange={(e) => setQuestionConfigForm({...questionConfigForm, update_frequency: e.target.value})}
                      >
                        <option value="weekly">Weekly (Every Sunday at 2 AM)</option>
                        <option value="fortnightly">Fortnightly (1st & 15th at 2 AM)</option>
                        <option value="monthly">Monthly (1st of month at 2 AM)</option>
                      </select>
                    </div>

                    <div>
                      <Label>Subjects to Update</Label>
                      <div className="mt-2 space-y-2">
                        {['Mathematics', 'Science', 'English', 'History', 'Geography'].map((subject) => (
                          <label key={subject} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={questionConfigForm.subjects.includes(subject)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setQuestionConfigForm({
                                    ...questionConfigForm,
                                    subjects: [...questionConfigForm.subjects, subject]
                                  });
                                } else {
                                  setQuestionConfigForm({
                                    ...questionConfigForm,
                                    subjects: questionConfigForm.subjects.filter(s => s !== subject)
                                  });
                                }
                              }}
                              className="rounded"
                            />
                            <span>{subject}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label>Grade Levels</Label>
                      <div className="mt-2 space-y-2">
                        {['6th', '7th', '8th', '9th', '10th', '11th', '12th'].map((grade) => (
                          <label key={grade} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={questionConfigForm.grade_levels.includes(grade)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setQuestionConfigForm({
                                    ...questionConfigForm,
                                    grade_levels: [...questionConfigForm.grade_levels, grade]
                                  });
                                } else {
                                  setQuestionConfigForm({
                                    ...questionConfigForm,
                                    grade_levels: questionConfigForm.grade_levels.filter(g => g !== grade)
                                  });
                                }
                              }}
                              className="rounded"
                            />
                            <span>{grade}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label>Countries</Label>
                      <div className="mt-2 space-y-2">
                        {['United States', 'United Kingdom', 'Australia', 'India', 'Canada'].map((country) => (
                          <label key={country} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={questionConfigForm.countries.includes(country)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setQuestionConfigForm({
                                    ...questionConfigForm,
                                    countries: [...questionConfigForm.countries, country]
                                  });
                                } else {
                                  setQuestionConfigForm({
                                    ...questionConfigForm,
                                    countries: questionConfigForm.countries.filter(c => c !== country)
                                  });
                                }
                              }}
                              className="rounded"
                            />
                            <span>{country}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button onClick={handleSaveQuestionConfig} disabled={loading}>
                        {loading ? 'Saving...' : 'Save Configuration'}
                      </Button>
                      <Button variant="outline" onClick={handleRunUpdaterNow} disabled={loading}>
                        Run Update Now
                      </Button>
                    </div>
                  </div>
                </Card>

                {questionConfig && (
                  <Card className="p-6 border border-gray-200">
                    <h4 className="font-semibold mb-4">Current Status</h4>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium">Last Run:</span> {questionConfig.last_run ? new Date(questionConfig.last_run).toLocaleString() : 'Never'}</p>
                      <p><span className="font-medium">Next Scheduled Run:</span> {questionConfig.next_run ? new Date(questionConfig.next_run).toLocaleString() : 'Not scheduled'}</p>
                      <p><span className="font-medium">Frequency:</span> {questionConfig.update_frequency}</p>
                      <p><span className="font-medium">Active Subjects:</span> {questionConfig.subjects.join(', ')}</p>
                      <p><span className="font-medium">Grade Levels:</span> {questionConfig.grade_levels.join(', ')}</p>
                      <p><span className="font-medium">Countries:</span> {questionConfig.countries.join(', ')}</p>
                    </div>
                  </Card>
                )}

                <Card className="p-6 border border-blue-200 bg-blue-50">
                  <h4 className="font-semibold text-blue-900 mb-2">ℹ️ How it works</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• The system automatically generates fresh questions based on your configuration</li>
                    <li>• Questions are curriculum-aligned and cover various difficulty levels</li>
                    <li>• New questions are added to the database, existing questions are preserved</li>
                    <li>• Each update generates approximately 21 questions per subject/grade/country combination</li>
                    <li>• You can manually trigger an update anytime using "Run Update Now"</li>
                  </ul>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </Card>

        {/* Delete User Dialog */}
        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete User</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>{deleteDialog.userName}</strong>? 
                This action cannot be undone. All associated data (progress, chat history, mappings) will be permanently deleted.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteUser}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete User
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Reset Password Dialog */}
        <AlertDialog open={resetPasswordDialog.open} onOpenChange={(open) => setResetPasswordDialog({ ...resetPasswordDialog, open })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset Password</AlertDialogTitle>
              <AlertDialogDescription>
                Set a new password for <strong>{resetPasswordDialog.userName}</strong>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                placeholder="Enter new password (min 6 characters)"
                value={resetPasswordDialog.newPassword}
                onChange={(e) => setResetPasswordDialog({ ...resetPasswordDialog, newPassword: e.target.value })}
                className="mt-2"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleResetPassword}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Reset Password
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Change Grade Dialog */}
        <AlertDialog open={gradeDialog.open} onOpenChange={(open) => setGradeDialog({ ...gradeDialog, open })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Change Student Grade</AlertDialogTitle>
              <AlertDialogDescription>
                Update grade for <strong>{gradeDialog.studentName}</strong>
                <br />
                Current Grade: <strong>{gradeDialog.currentGrade}</strong>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4">
              <Label htmlFor="new-grade">New Grade</Label>
              <Input
                id="new-grade"
                type="text"
                placeholder="e.g., 9th, 10th, 11th"
                value={gradeDialog.newGrade}
                onChange={(e) => setGradeDialog({ ...gradeDialog, newGrade: e.target.value })}
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-2">
                This will automatically update quiz generation and all reports for this student
              </p>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleUpdateGrade}
                className="bg-green-600 hover:bg-green-700"
              >
                Update Grade
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Edit Mapping Dialog */}
        <AlertDialog open={mappingEditDialog.open} onOpenChange={(open) => setMappingEditDialog({ ...mappingEditDialog, open })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Edit Mapping</AlertDialogTitle>
              <AlertDialogDescription>
                Update mappings for <strong>{mappingEditDialog.studentName}</strong>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4 space-y-3">
              <div>
                <Label htmlFor="edit-parent">Parent</Label>
                <select
                  id="edit-parent"
                  className="w-full mt-1 p-2 border rounded"
                  value={mappingEditDialog.mapping?.parent_id || ''}
                  onChange={(e) => setMappingEditDialog({
                    ...mappingEditDialog,
                    mapping: { ...mappingEditDialog.mapping, parent_id: e.target.value }
                  })}
                >
                  <option value="">None</option>
                  {getParents().map(p => (
                    <option key={p.id} value={p.id}>{p.profile?.name || p.email}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="edit-teacher">Teacher</Label>
                <select
                  id="edit-teacher"
                  className="w-full mt-1 p-2 border rounded"
                  value={mappingEditDialog.mapping?.teacher_id || ''}
                  onChange={(e) => setMappingEditDialog({
                    ...mappingEditDialog,
                    mapping: { ...mappingEditDialog.mapping, teacher_id: e.target.value }
                  })}
                >
                  <option value="">None</option>
                  {getTeachers().map(t => (
                    <option key={t.id} value={t.id}>{t.profile?.name || t.email}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="edit-counselor">Counselor</Label>
                <select
                  id="edit-counselor"
                  className="w-full mt-1 p-2 border rounded"
                  value={mappingEditDialog.mapping?.counselor_id || ''}
                  onChange={(e) => setMappingEditDialog({
                    ...mappingEditDialog,
                    mapping: { ...mappingEditDialog.mapping, counselor_id: e.target.value }
                  })}
                >
                  <option value="">None</option>
                  {getCounselors().map(c => (
                    <option key={c.id} value={c.id}>{c.profile?.name || c.email}</option>
                  ))}
                </select>
              </div>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleUpdateMapping}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Update Mapping
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default SuperuserDashboard;
